import { useCallback } from 'react';
import { Company, CompanyType, Team, User } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Option } from 'components/atoms/select';
import {
  CompanyOption,
  Extra,
  UserOption,
} from 'components/molecules/squad-picker';
import { useAllCompanies } from 'queries/account';
import { useUsersQuery } from 'queries/members';

interface Result {
  getUserAssigned: (
    placeholder: Team,
  ) => { user: User; option: Option<Extra> } | undefined;
  getCompanyAssigned: (
    placeholder: Team,
  ) => { company: Company; option: Option<Extra> } | undefined;
  getResponsibleCompany: (placeholder: Team) => Company | undefined;
  getResponsibleCompanyForOption: (
    option: Option<Extra>,
  ) => Company | undefined;
  doesCompanyHaveAcccesForPrivateTasks: (company: Company) => {
    hasAccess: boolean;
    permissionSetName?: PartnerPermissionName;
  };
}
export const usePlaceholderValueHelpers = (): Result => {
  const { data: companies } = useAllCompanies();
  const { data: users } = useUsersQuery();
  const getUserAssigned = useCallback(
    (placeholder: Team) => {
      const assignedUserId = placeholder.userList?.[0]?.userId;

      if (assignedUserId != null) {
        const assignedUser = users?.find(
          ({ userId }) => userId === assignedUserId,
        );

        if (assignedUser != null) {
          return {
            user: assignedUser,
            option: UserOption(assignedUser),
          };
        }
      }

      return undefined;
    },
    [users],
  );
  const getCompanyAssigned = useCallback(
    (placeholder: Team) => {
      const assignedCompanyId = placeholder.companyList?.[0]?.companyId;

      if (assignedCompanyId != null) {
        const assignedCompany = companies?.find(
          ({ companyId }) => companyId === assignedCompanyId,
        );

        if (assignedCompany != null) {
          return {
            company: assignedCompany,
            option: CompanyOption(assignedCompany),
          };
        }
      }

      return undefined;
    },
    [companies],
  );
  const getResponsibleCompany = useCallback(
    (placeholder: Team) =>
      getCompanyAssigned(placeholder)?.company ??
      getUserAssigned(placeholder)?.user.company,
    [getCompanyAssigned, getUserAssigned],
  );
  const getResponsibleCompanyForOption = useCallback(
    (option: Option<Extra>): Company | undefined => {
      const userId = option.extra?.type === 'user' ? option.extra?.id : null;
      const companyId =
        option.extra?.type === 'company' ? option.extra?.id : null;

      if (userId != null) {
        return users?.find(({ userId: x }) => x === userId)?.company;
      }
      if (companyId != null) {
        return companies?.find(({ companyId: x }) => companyId === x);
      }

      return undefined;
    },
    [companies, users],
  );
  const doesCompanyHaveAcccesForPrivateTasks = useCallback(
    (company: Company) => {
      const _company = companies?.find(
        ({ companyId: x }) => company.companyId === x,
      );

      if (_company == null || _company.companyType !== CompanyType.Partner) {
        return {
          hasAccess: true,
        };
      }
      const partnerPermissionSetName =
        _company?.accessLevel?.partnerPermissionSetName;

      const hasAccess =
        partnerPermissionSetName !== PartnerPermissionName.Restricted &&
        partnerPermissionSetName !== PartnerPermissionName.Moderate;

      return {
        hasAccess,
        permissionSetName: partnerPermissionSetName,
      };
    },
    [companies],
  );

  return {
    getUserAssigned,
    getCompanyAssigned,
    getResponsibleCompany,
    getResponsibleCompanyForOption,
    doesCompanyHaveAcccesForPrivateTasks,
  };
};
